import { z } from 'zod';
import { idSchema, patchSchema } from './common.ts';

/**
 * Connectors ("Konnektorlar") — the data sources of sensors. A connector discovers the source's sensors (nodes →
 * channels / columns with their last raw value) and streams their values; each source sensor is bound to one of our
 * physical indicators. The O'zbekgidroenergo reservoirs share one organization-level connector to the ATNT unified
 * server ("One Server", docs/research/unified-server.md); other organizations plug their own backends in through the
 * generic types (REST, MQTT, OPC UA, Modbus TCP, webhook, file import).
 *
 * Scope: `organization` connectors belong to the organization and are visible in every tenant of it (editable by
 * organization / platform admins only; per-tenant settings such as the unified-server site code stay on the tenant);
 * `tenant` connectors belong to one reservoir (admin module `connectors` CRUD).
 *
 * Endpoints the API implements (mounted under /api/v1/tenants/:tenantId, admin module `connectors`):
 *   GET    /connectors                                 → ConnectorDto[]   organization connectors of the tenant's org + the tenant's own
 *   POST   /connectors                                 CreateConnectorInput → ConnectorDto
 *   GET    /connectors/:id                             → ConnectorDto
 *   PATCH  /connectors/:id                             UpdateConnectorInput → ConnectorDto   (secrets omitted = kept)
 *   DELETE /connectors/:id                             → 204
 *   POST   /connectors/test                            TestConnectorInput (draft config) → ConnectorTestResult
 *   POST   /connectors/:id/test                        TestConnectorInput (optional draft, stored secrets merged) → ConnectorTestResult
 *   GET    /connectors/:id/sensors                     → SourceSensorDto[]
 *   PUT    /connectors/:id/sensors/:sensorId/binding   BindSourceSensorInput → SourceSensorDto
 *   POST   /connectors/:id/sensors/import              ImportSourceSensorsInput → ConnectorImportResult
 *   GET    /connectors/:id/log?level&limit             → ConnectorLogEntry[] (newest first)
 * Secrets (API keys, tokens, passwords, webhook secrets) are write-only: inputs carry the plain value, DTOs only a
 * `SecretHint`. Enable / disable is `PATCH { enabled }`.
 */

export const CONNECTOR_TYPES = ['atnt_unified', 'rest', 'mqtt', 'opcua', 'modbus_tcp', 'webhook', 'file_import'] as const;
export type ConnectorType = (typeof CONNECTOR_TYPES)[number];
export const connectorTypeSchema = z.enum(CONNECTOR_TYPES);

export const CONNECTOR_SCOPES = ['organization', 'tenant'] as const;
export type ConnectorScope = (typeof CONNECTOR_SCOPES)[number];

export const CONNECTOR_STATUSES = ['connected', 'error', 'disabled', 'checking'] as const;
export type ConnectorStatus = (typeof CONNECTOR_STATUSES)[number];

export const CONNECTOR_LOG_LEVELS = ['info', 'warning', 'error'] as const;
export type ConnectorLogLevel = (typeof CONNECTOR_LOG_LEVELS)[number];

/** Unified-server site code (`sites.code`: andijon, chorvoq, chotqol, hisorak, ohangaron, topolang). */
export const CONNECTOR_SITE_CODE_RE = /^[a-z0-9][a-z0-9_-]{0,127}$/;

const pollIntervalSec = z.number().int().min(10).max(86_400);
const httpUrl = z.url({ protocol: /^https?$/ }).max(500);
/** Write-only secret: send it to set / replace the stored value, omit it to keep the stored one. */
const secretInput = z.string().min(1).max(1000);
/** API path relative to the base URL ("/sensors"). */
const apiPath = z.string().trim().min(1).max(300).regex(/^\/\S*$/);
/** JSONPath into one item of the response ("$.id", "$.data[*].value"). */
const jsonPath = z.string().trim().min(1).max(200).regex(/^\$[\w.[\]*'"-]*$/);
const optionalText = (max: number) => z.string().trim().max(max).nullish();

/* ───────────── per-type configuration (input) ───────────── */

export const atntUnifiedConfigSchema = z.object({
  type: z.literal('atnt_unified'),
  /** e.g. https://monitoring.uzgidro.uz/api (with the /api prefix) */
  baseUrl: httpUrl,
  /** `X-API-Key: atnt.<keyId>.<secret>` issued in the unified server ("Tashqi tizim kalitlari") */
  apiKey: secretInput.optional(),
  /** site code of the tenant; for an organization connector it is per tenant (`tenants.externalSiteId`) */
  siteCode: z.string().trim().regex(CONNECTOR_SITE_CODE_RE).nullish(),
  pollIntervalSec: pollIntervalSec.default(300),
});

export const REST_AUTH_KINDS = ['none', 'header', 'bearer'] as const;
export type RestAuthKind = (typeof REST_AUTH_KINDS)[number];
export const restAuthSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('none') }),
  z.object({
    kind: z.literal('header'),
    headerName: z.string().trim().min(1).max(100).regex(/^[A-Za-z0-9-]+$/),
    value: secretInput.optional(),
  }),
  z.object({ kind: z.literal('bearer'), token: secretInput.optional() }),
]);

export const restConfigSchema = z.object({
  type: z.literal('rest'),
  baseUrl: httpUrl,
  auth: restAuthSchema.default({ kind: 'none' }),
  /** sensor catalog, e.g. /sensors */
  sensorsPath: apiPath,
  /** latest values, e.g. /readings/latest */
  valuesPath: apiPath,
  idPath: jsonPath.default('$.id'),
  valuePath: jsonPath.default('$.value'),
  timePath: jsonPath.default('$.timestamp'),
  pollIntervalSec: pollIntervalSec.default(60),
});

export const MQTT_PAYLOAD_FORMATS = ['json', 'csv', 'plain'] as const;
export const mqttConfigSchema = z.object({
  type: z.literal('mqtt'),
  brokerUrl: z.url({ protocol: /^(mqtts?|wss?)$/ }).max(500),
  /** topic filter with + / # wildcards; the matched topic is the sensor id */
  topic: z.string().trim().min(1).max(300),
  username: optionalText(200),
  password: secretInput.optional(),
  payloadFormat: z.enum(MQTT_PAYLOAD_FORMATS).default('json'),
});

export const OPCUA_SECURITY_MODES = ['none', 'sign', 'sign_encrypt'] as const;
export const opcuaConfigSchema = z.object({
  type: z.literal('opcua'),
  endpointUrl: z.url({ protocol: /^opc\.tcp$/ }).max(500),
  securityMode: z.enum(OPCUA_SECURITY_MODES).default('none'),
  username: optionalText(200),
  password: secretInput.optional(),
  /** node ids to read, e.g. ns=2;s=Dam.Level */
  nodeIds: z.array(z.string().trim().min(1).max(300)).min(1).max(500),
  pollIntervalSec: pollIntervalSec.default(30),
});

export const MODBUS_REGISTER_KINDS = ['holding', 'input', 'coil', 'discrete'] as const;
export const MODBUS_DATA_TYPES = ['int16', 'uint16', 'int32', 'uint32', 'float32', 'bool'] as const;
export const modbusRegisterSchema = z.object({
  address: z.number().int().min(0).max(65_535),
  name: z.string().trim().min(1).max(120),
  kind: z.enum(MODBUS_REGISTER_KINDS).default('holding'),
  dataType: z.enum(MODBUS_DATA_TYPES).default('uint16'),
  /** raw × scale */
  scale: z.number().finite().default(1),
  unit: optionalText(32),
});
export type ModbusRegister = z.output<typeof modbusRegisterSchema>;

export const modbusTcpConfigSchema = z.object({
  type: z.literal('modbus_tcp'),
  host: z
    .string()
    .trim()
    .min(1)
    .max(253)
    .regex(/^[A-Za-z0-9]([A-Za-z0-9.-]*[A-Za-z0-9])?$/),
  port: z.number().int().min(1).max(65_535).default(502),
  unitId: z.number().int().min(0).max(255).default(1),
  registers: z.array(modbusRegisterSchema).min(1).max(500),
  pollIntervalSec: pollIntervalSec.default(10),
});

export const WEBHOOK_PAYLOAD_FORMATS = ['json', 'csv'] as const;
export const webhookConfigSchema = z.object({
  type: z.literal('webhook'),
  payloadFormat: z.enum(WEBHOOK_PAYLOAD_FORMATS).default('json'),
  /** HMAC secret of the `X-Signature` header; the client may send a generated one (omit = keep / server generates) */
  secret: z.string().min(16).max(200).optional(),
});

export const FILE_IMPORT_FORMATS = ['csv', 'xlsx'] as const;
export const FILE_IMPORT_DELIMITERS = [',', ';', '\t'] as const;
export const fileImportColumnSchema = z.object({
  /** column header in the file */
  column: z.string().trim().min(1).max(120),
  /** display name of the source sensor (defaults to the column) */
  name: optionalText(160),
  unit: optionalText(32),
});
export const fileImportConfigSchema = z.object({
  type: z.literal('file_import'),
  format: z.enum(FILE_IMPORT_FORMATS).default('csv'),
  delimiter: z.enum(FILE_IMPORT_DELIMITERS).default(','),
  decimalComma: z.boolean().default(false),
  headerRow: z.number().int().min(1).max(100).default(1),
  timeColumn: z.string().trim().min(1).max(120),
  timeFormat: z.string().trim().min(1).max(64).default('YYYY-MM-DD HH:mm:ss'),
  columns: z.array(fileImportColumnSchema).min(1).max(1000),
});

export const connectorConfigInputSchema = z.discriminatedUnion('type', [
  atntUnifiedConfigSchema,
  restConfigSchema,
  mqttConfigSchema,
  opcuaConfigSchema,
  modbusTcpConfigSchema,
  webhookConfigSchema,
  fileImportConfigSchema,
]);
export type ConnectorConfigInput = z.input<typeof connectorConfigInputSchema>;
export type ConnectorConfig = z.output<typeof connectorConfigInputSchema>;

/* ───────────── per-type configuration (DTO: secrets masked) ───────────── */

/** What a DTO shows of a write-only secret: whether it is set and a non-secret hint ("atnt.k3Jd8Qm2.••••", "••••Q1sV"). */
export interface SecretHint {
  set: boolean;
  hint: string | null;
}

type Cfg<T extends ConnectorType> = Extract<ConnectorConfig, { type: T }>;
export type RestAuthDto = { kind: 'none' } | { kind: 'header'; headerName: string; value: SecretHint } | { kind: 'bearer'; token: SecretHint };

export type ConnectorConfigDto =
  | (Omit<Cfg<'atnt_unified'>, 'apiKey' | 'siteCode'> & { apiKey: SecretHint; siteCode: string | null })
  | (Omit<Cfg<'rest'>, 'auth'> & { auth: RestAuthDto })
  | (Omit<Cfg<'mqtt'>, 'password' | 'username'> & { username: string | null; password: SecretHint })
  | (Omit<Cfg<'opcua'>, 'password' | 'username'> & { username: string | null; password: SecretHint })
  | Cfg<'modbus_tcp'>
  | (Omit<Cfg<'webhook'>, 'secret'> & { /** absolute URL the source POSTs to */ endpointUrl: string; secret: SecretHint })
  | Cfg<'file_import'>;

/* ───────────── connector ───────────── */

export const createConnectorSchema = z.object({
  /** optional client-generated id: idempotent create, lets the form show the webhook URL before saving */
  id: idSchema.optional(),
  name: z.string().trim().min(1).max(160),
  description: optionalText(1000),
  /** `organization` needs an organization / platform admin */
  scope: z.enum(CONNECTOR_SCOPES).default('tenant'),
  enabled: z.boolean().default(true),
  config: connectorConfigInputSchema,
});
/** Scope and type are fixed after creation (`config.type` must match the stored type). */
export const updateConnectorSchema = patchSchema(createConnectorSchema.omit({ id: true, scope: true }));
export type CreateConnectorInput = z.input<typeof createConnectorSchema>;
export type UpdateConnectorInput = z.input<typeof updateConnectorSchema>;

export interface ConnectorDto {
  id: string;
  name: string;
  description: string | null;
  type: ConnectorType;
  scope: ConnectorScope;
  organizationId: string;
  /** null for organization connectors */
  tenantId: string | null;
  enabled: boolean;
  status: ConnectorStatus;
  lastSyncAt: string | null;
  lastError: string | null;
  /** source sensors discovered for this tenant / bound to one of its indicators */
  sensorsTotal: number;
  sensorsBound: number;
  config: ConnectorConfigDto;
  createdBy: { id: string; fullName: string } | null;
  createdAt: string;
  updatedAt: string;
}

export const testConnectorSchema = z.object({ config: connectorConfigInputSchema.optional() });
export type TestConnectorInput = z.input<typeof testConnectorSchema>;

export interface ConnectorTestResult {
  ok: boolean;
  latencyMs: number | null;
  /** sensors the source reported (null when the check failed before the catalog) */
  sensorsFound: number | null;
  message: string | null;
  checkedAt: string;
}

/* ───────────── source sensors ───────────── */

export interface SourceSensorBinding {
  indicatorId: string;
  deviceId: string;
}

/** A sensor as the source reports it (unified server: `SiteSensor` = one CSV column of a node directory). */
export interface SourceSensorDto {
  id: string;
  /** node / device / gateway group ("greben_gateway/anl4", "mLog", MQTT topic prefix…) */
  node: string;
  columnName: string;
  channel: string | null;
  /** signal type from the source ("Current loop [mA]") */
  typeLabel: string | null;
  unit: string | null;
  lastValue: number | null;
  lastValueAt: string | null;
  binding: SourceSensorBinding | null;
}

/** `indicatorId: null` unbinds. The indicator must be a physical indicator of the tenant. */
export const bindSourceSensorSchema = z.object({ indicatorId: idSchema.nullable() });
export type BindSourceSensorInput = z.input<typeof bindSourceSensorSchema>;

/** Create a device per node (and a physical indicator per sensor) for unbound sensors, then bind them. */
export const importSourceSensorsSchema = z.object({
  sensorIds: z.array(z.string().min(1).max(200)).min(1).max(1000),
  categoryId: idSchema.nullish(),
  locationId: idSchema.nullish(),
});
export type ImportSourceSensorsInput = z.input<typeof importSourceSensorsSchema>;

export interface ConnectorImportResult {
  devicesCreated: number;
  indicatorsCreated: number;
  sensorsBound: number;
}

/* ───────────── log ───────────── */

export interface ConnectorLogEntry {
  id: string;
  at: string;
  level: ConnectorLogLevel;
  /** machine code the UI translates ("sync.ok", "auth.failed"…); `message` is the untranslated fallback */
  code: string | null;
  message: string;
  params?: Record<string, string | number>;
}
